// Display name helper: prefers "Nickname LastName", falls back to "FirstName LastName"
export const displayName = (person?: { nickname?: string; lastName?: string; firstName?: string } | null): string => {
  if (!person) return '—';
  const first = person.nickname || person.firstName || '';
  const last = person.lastName || '';
  return [first, last].filter(Boolean).join(' ') || '—';
};

// User and Authentication Types
export interface User {
  id: string;
  email: string;
  role: UserRole;
  employee?: Employee;
  mustChangePassword?: boolean;
}

export enum UserRole {
  HR_ADMIN = 'hr_admin',
  HR_MANAGER = 'hr_manager',
  MANAGER = 'manager',
  EMPLOYEE = 'employee',
  RECRUITER = 'recruiter',
  FINANCE = 'finance',
}

// Core HR Types
export interface Entity {
  id: string;
  code: string;
  name: string;
  country: string;
  currency: string;
  registrationId?: string;
}

export interface Department {
  id: string;
  entityId: string;
  entity?: Entity;
  code: string;
  name: string;
  parentId?: string;
  parent?: Department;
  children?: Department[];
}

export interface Position {
  id: string;
  entityId: string;
  entity?: Entity;
  departmentId?: string;
  department?: Department;
  title: string;
  level?: string;
  employmentTypeDefault?: string;
  standardHoursPerWeek?: number;
  defaultProbationMonths?: number;
  description?: string;
}

export interface Employee {
  id: string;
  employeeNumber?: string;
  entityId: string;
  entity?: Entity;
  departmentId?: string;
  department?: Department;
  positionId?: string;
  position?: Position;
  managerId?: string;
  manager?: Employee;
  directReports?: Employee[];
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
  personalEmail?: string;
  phoneNumber?: string;
  phoneRegionCode?: string;
  country?: string;
  dateOfBirth?: string;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  employmentStatus: EmploymentStatus;
  access?: EmployeeAccess;
  startDate?: string;
  endDate?: string;
  workLocation?: string;
  workMode?: string;
  salaryCurrency?: string;
  monthlySalary?: number;
  fullName: string;
}

export enum EmployeeAccess {
  APPROVER = 'approver',
}

export enum EmploymentStatus {
  ACTIVE = 'active',
  TERMINATED = 'terminated',
  ON_LEAVE = 'on_leave',
}

// Leave Types
export interface LeaveType {
  id: string;
  name: string;
  code: string;
  isPaid: boolean;
  requiresApproval: boolean;
  impactsPayroll: boolean;
  description?: string;
  isActive: boolean;
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  employee?: Employee;
  leaveTypeId: string;
  leaveType?: LeaveType;
  startDate: string;
  endDate: string;
  totalDays: number;
  isHalfDayStart: boolean;
  isHalfDayEnd: boolean;
  status: LeaveRequestStatus;
  reason?: string;
  approverId?: string;
  approver?: Employee;
  decidedAt?: string;
  approverComments?: string;
  attachmentUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum LeaveRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
}

export interface LeaveBalance {
  id: string;
  employeeId: string;
  employee?: Employee;
  leaveTypeId: string;
  leaveType?: LeaveType;
  year: number;
  openingBalanceDays: number;
  accruedDays: number;
  usedDays: number;
  adjustedDays: number;
  closingBalanceDays: number;
  lastAccrualDate?: string;
  carryOverDays?: number;
  carryOverExpiry?: string;
}

// API Response Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  user: User;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}